import * as _ from 'lodash';
import * as React from 'react';
import { css } from 'aphrodite';

import { diagnostics } from '@blend/ui-common';
import * as autoForm from 'login-utils/autoForm';
import * as autoLoanUtils from 'login-utils/autoLoanUtils';
import AutoLeaseBuyoutSVG from 'login-components/Common/svg/AutoLeaseBuyoutSVG';
import AutoPurchaseSVG from 'login-components/Common/svg/AutoPurchaseSVG';
import AutoRefinanceSVG from 'login-components/Common/svg/AutoRefinanceSVG';
import { DataShape, Field, FormProps, reduxForm } from 'redux-form';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import { connect } from 'react-redux';
import history from 'login-utils/history';
import LargeIconButtonGroup from 'login-components/Common/LargeIconButtonGroup/LargeIconButtonGroup';
import { LargeIconButtonConfig } from 'login-components/Common/Buttons/LargeIconButton';
import LoadingSpinner from 'login-components/Common/LoadingSpinner/LoadingSpinner';
import { paneFormStyles } from 'login-components/Common/PaneLayout/paneStyles';
import States from 'login-constants/states';
import * as autoLoansAPI from 'login-apis/autoLoansAPI';

const autoLoanPurposeConfigDefinitions: Record<AutoLoanPurposeType, LargeIconButtonConfig> = {
  PURCHASE: {
    value: 'PURCHASE',
    iconComponent: AutoPurchaseSVG,
    buttonId: 'it-chooseAutoLoanPurposePurchase',
    text: 'Purchase',
    subText: 'Buy a new or used car.',
  },
  REFINANCE: {
    value: 'REFINANCE',
    iconComponent: AutoRefinanceSVG,
    buttonId: 'it-chooseAutoLoanPurposeRefinance',
    text: 'Refinance',
    subText: 'Change your loan terms, get cash out, or both.',
  },
  LEASE_BUYOUT: {
    value: 'LEASE_BUYOUT',
    iconComponent: AutoLeaseBuyoutSVG,
    buttonId: 'it-chooseAutoLoanPurposeBuyout',
    text: 'Lease buyout',
    subText: 'Buy the car you’re leasing from the dealership.',
  },
};

const initialValues: DataShape = {
  autoLoanPurposeType: null,
  autoRefinanceType: null,
};

export type ChooseAutoLoanPurposeProps = ReactRouter.RouteComponentProps<{}, {}>;

interface ChooseAutoLoanPurposeState {
  isSubmitting: boolean;
  error: string;
  loanPurposeTypes: AutoLoanPurposeType[];
}

export class ChooseAutoLoanPurpose extends React.Component<
  ChooseAutoLoanPurposeProps & FormProps<DataShape, ChooseAutoLoanPurposeProps, ReduxState>,
  ChooseAutoLoanPurposeState
> {
  state = {
    isSubmitting: false,
    error: '',
    loanPurposeTypes: [],
  };

  async componentDidMount() {
    try {
      const loginAppConfig = await autoLoansAPI.getConfig();
      const loanPurposeTypes = loginAppConfig.AUTO.loanPurposeTypes;
      this.setState({ loanPurposeTypes });
    } catch (err) {
      // istanbul ignore next
      diagnostics.error(err);
    }
  }

  onSelect = async value => {
    if (value === 'REFINANCE') {
      history.push(States.AUTO_REFINANCE_TYPE);
    } else if (autoLoanUtils.shouldCreateOrUpdateAutoLoan()) {
      this.setState({
        error: '',
        isSubmitting: true,
      });
      try {
        await autoLoanUtils.createOrUpdateAutoLoan({
          autoLoanPurposeType: value,
        });
      } catch (err) {
        this.setState({
          error: 'Failed to create application, please try again',
          // Only unset on error, since we navigate away in the success case
          // and don't want the user to be able to submit again
          isSubmitting: false,
        });
      }
    } else {
      history.push(States.CREATE_ACCOUNT);
    }
  };

  render() {
    const { error, isSubmitting, loanPurposeTypes } = this.state;

    if (isSubmitting) {
      return (
        <div className={css(paneFormStyles.loadingSpinnerWrapper)}>
          <LoadingSpinner />
        </div>
      );
    }

    // avoid render until loanPurposeTypes are populated
    if (_.isEmpty(loanPurposeTypes)) return null;
    const autoLoanPurposeConfigs = _.map(
      loanPurposeTypes,
      loanPurposeType => autoLoanPurposeConfigDefinitions[loanPurposeType],
    );

    return (
      <React.Fragment>
        {error && (
          <div className={css(paneFormStyles.errorWrapper)}>
            <ErrorMessage errorText={error} />
          </div>
        )}
        <Field
          name={autoForm.FIELDS.autoLoanPurposeType}
          configs={autoLoanPurposeConfigs}
          onSelect={this.onSelect}
          component={LargeIconButtonGroup}
        />
      </React.Fragment>
    );
  }
}

const withReduxForm = reduxForm<
  FormProps<DataShape, ChooseAutoLoanPurposeProps, ReduxState>,
  ChooseAutoLoanPurposeProps,
  ReduxState
>({
  initialValues,
  ...autoForm.FORM_CONFIG,
})(ChooseAutoLoanPurpose);

export default connect<{}, {}, ChooseAutoLoanPurposeProps>(
  null,
  {},
)(withReduxForm);
